<!-- eslint-disable vue/no-mutating-props -->
<template>
    <v-row>
        <v-col cols="3">
            <v-autocomplete
                ref="jobNumberField"
                v-model="complaint.jobNumber"
                :items="jobNumbersWithInput"
                label="Numer zlecenia"
                item-value="primaryNumber"
                item-text="primaryNumber"
                chips
                clearable
                :no-data-text="jobNumbersSearchNoDataText"
                :loading="jobNumberSearchData.isLoading"
                :search-input.sync="jobNumberSearchData.search"
                :disabled="isReadOnly"
                :error="isFieldInvalid('jobNumber')"
                :error-messages="validationMessages('jobNumber')"
                @change="handleJobNumberChange"
                @blur="triggerBlur('jobNumber')"
            />
        </v-col>
        <v-col cols="6">
            <v-autocomplete
                ref="orderNumbersField"
                v-model="complaint.complaintOrderComplaint"
                :items="orderNumbers"
                label="Numery zamówień"
                item-value="orderNumber"
                item-text="orderNumber"
                no-data-text="Nie znaleziono numerów zamówień"
                clearable
                multiple
                chips
                :disabled="isReadOnly || !canPickOrderNumbers"
                @change="handleOrderNumbersChange"
            />
        </v-col>
        <v-col cols="3">
            <v-text-field
                :value="selectedProductTypes"
                label="Rodzaj produktu"
                readonly
                disabled
            />
        </v-col>
    </v-row>
</template>

<script>
import { createSearchState } from '../../helpers/searchState'

/* eslint-disable vue/no-mutating-props */
export default {
    name: 'DetailsTabJobSection',

    props: {
        complaint: {
            type: Object,
            required: true,
        },
        validation: {
            type: Object,
            default: () => ({}),
        },
        storeModuleName: {
            type: String,
            default: '',
        },
        isReadOnly: {
            type: Boolean,
        },
    },

    data() {
        return {
            jobNumberSearchData: createSearchState(),
            scanData: {
                buffer: '',
                timer: null,
                interKeyTimeout: 60,
                isActive: false,
                lastTs: 0,
                fastCount: 0,
                minToLock: 3
            },
        }
    },

    computed: {
        validationState() {
            const fields = ['jobNumber']
            const provided = this.validation || {}

            return fields.reduce((acc, field) => {
                const entry = provided[field] || {}

                acc[field] = {
                    error: Boolean(entry.error),
                    messages: Array.isArray(entry.messages) ? entry.messages : [],
                }

                return acc
            }, {})
        },

        jobNumbers() {
            return this.$store.state.complaints.internalComplaints.reportItems
        },

        selectedReportItem() {
            return this.$store.state.complaints.internalComplaints.reportItems
                ?.find(item => item.primaryNumber === this.complaint.jobNumber)
        },

        orderNumbers() {
            return this.selectedReportItem?.itemPlatesOrdersNumbers ?? []
        },

        orderNumberToProductTypeMap() {
            return this.orderNumbers.reduce((accumulator, item) => {
                accumulator[item.orderNumber] = item.productTypeName || item.productType || ''

                return accumulator
            }, {})
        },

        selectedProductTypes() {
            const selectedOrderDetails = Array.isArray(this.complaint.complaintOrderComplaintDetails)
                ? this.complaint.complaintOrderComplaintDetails
                : []
            const productTypes = selectedOrderDetails
                .map(item => item.productTypeName)
                .filter(Boolean)

            return productTypes.join(', ')
        },

        jobNumbersWithInput() {
            const jobNumbersList = this.jobNumbers
            const currentJobNumber = (this.complaint.jobNumber || '').trim()
            const alreadyExists = jobNumbersList.some(item => item.primaryNumber === currentJobNumber)

            if (!currentJobNumber || alreadyExists) return jobNumbersList

            return [{ primaryNumber: currentJobNumber }, ...jobNumbersList]
        },

        jobNumbersSearchNoDataText() {
            if (this.jobNumberSearchData.isLoading) return 'Szukanie...'

            const search = this.jobNumberSearchData.search

            return !search || search.length < 3 
                ? 'Wpisz conajmniej 3 znaki żeby wyszukać'
                : 'Nie znaleziono zleceń'
        },

        canPickOrderNumbers() {
            return Boolean(this.complaint.jobNumber)
        },
    },

    watch: {
        'jobNumberSearchData.search'(value) {
            clearTimeout(this.jobNumberSearchData.timer)
            this.jobNumberSearchData.timer = setTimeout(async() => {
                const search = (value || '').trim()

                if (search.length < 3 || search === this.jobNumberSearchData.lastQuery) return

                this.jobNumberSearchData.isLoading = true

                await this.$store.dispatch(`${this.storeModuleName}/getReportItems`, search)

                this.jobNumberSearchData.lastQuery = search
                this.jobNumberSearchData.isLoading = false
            }, this.jobNumberSearchData.debounceTimer)
        },
    },

    mounted() {
        window.addEventListener('keydown', this.onGlobalKeydown, true)
    },

    beforeDestroy() {
        window.removeEventListener('keydown', this.onGlobalKeydown, true)
        clearTimeout(this.scanData.timer)
    },

    methods: {
        validationNode(field) {
            return this.validationState[field] || null
        },

        isFieldInvalid(field) {
            const node = this.validationNode(field)

            return Boolean(node && node.error)
        },

        validationMessages(field) {
            const node = this.validationNode(field)

            if (!node || !Array.isArray(node.messages)) return []

            return node.messages
        },

        triggerBlur(field) {
            this.$emit('field-blur', field)
        },

        async handleJobNumberChange() {
            this.complaint.complaintOrderComplaint = []
            this.complaint.complaintOrderComplaintDetails = []
            this.complaint.defects = []

            const jobNumber = (this.complaint.jobNumber || '').trim()

            if (jobNumber) {
                await this.$store.dispatch(`${this.storeModuleName}/getReportItems`, jobNumber)
            }
        },

        onGlobalKeydown(event) {
            const target = event.target
            const isEditable = target && (
                target.isContentEditable ||
                ['INPUT', 'TEXTAREA'].includes((target.tagName || '').toUpperCase())
            )

            const isJobFieldFocus = this.$refs.jobNumberField && this.$refs.jobNumberField.$el.contains(target)
            const isOrderFieldFocus = this.$refs.orderNumbersField && this.$refs.orderNumbersField.$el.contains(target)
            const isOurField = isJobFieldFocus || isOrderFieldFocus

            if (isEditable && !isOurField) return

            if (event.key === 'Enter') {
                if (this.scanData.isActive) event.preventDefault()

                this.processScan()

                return
            }

            if (!(event.key.length === 1 && !event.ctrlKey && !event.altKey && !event.metaKey)) return

            const now = performance.now()
            const dt = now - (this.scanData.lastTs || 0)
            const fast = dt > 0 && dt <= this.scanData.interKeyTimeout

            if (!this.scanData.isActive) {
                if (this.scanData.buffer === '' && event.key === '{') {
                    this.scanData.isActive = true
                } else if (fast) {
                    this.scanData.fastCount += 1
                    if (this.scanData.fastCount >= (this.scanData.minToLock - 1)) {
                        this.scanData.isActive = true
                    }
                } else {
                    this.scanData.fastCount = 0
                }
            }

            this.scanData.lastTs = now

            if (this.scanData.isActive) {
                event.preventDefault()
                this.scanData.buffer += event.key
                clearTimeout(this.scanData.timer)
                this.scanData.timer = setTimeout(() => this.processScan(true), this.scanData.interKeyTimeout)

                return
            }
        },

        processScan() {
            const raw = (this.scanData.buffer || '').trim()

            clearTimeout(this.scanData.timer)
            this.scanData.timer = null
            this.scanData.isActive = false
            this.scanData.fastCount = 0
            this.scanData.lastTs = 0
            this.scanData.buffer = ''

            if (!raw) return

            let parsedCode

            try {
                parsedCode = JSON.parse(raw)
            } catch {
                return
            }

            if (parsedCode?.type === 'KZ') {
                this.applyJobNumberFromScan(parsedCode)
            }

            if (parsedCode?.type === 'KP') {
                this.applyOrderNumberFromScan(parsedCode)
            }
        },

        async applyJobNumberFromScan(code) {
            this.complaint.jobNumber = ''
            this.complaint.complaintOrderComplaint = []
            this.complaint.complaintOrderComplaintDetails = []
            this.complaint.defects = []
            this.jobNumberSearchData.search = ''
            this.jobNumberSearchData.lastQuery = null

            this.jobNumberSearchData.search = code.taskNumber
            this.jobNumberSearchData.lastQuery = null

            this.$nextTick(() => {
                this.complaint.jobNumber = code.taskNumber
                this.$store.dispatch(`${this.storeModuleName}/getReportItems`, code.taskNumber)
            })
        },

        async applyOrderNumberFromScan(code) {
            if (!code.order) return

            if (!this.complaint.complaintOrderComplaint.includes(code.order)) {
                this.complaint.complaintOrderComplaint.push(code.order)
                this.handleOrderNumbersChange(this.complaint.complaintOrderComplaint)
            }
        },

        handleOrderNumbersChange(selectedOrderNumbersArg) {
            const selectedOrderNumbers = Array.isArray(selectedOrderNumbersArg)
                ? selectedOrderNumbersArg
                : []

            if (!selectedOrderNumbers.length) {
                this.complaint.complaintOrderComplaintDetails = []

                return
            }

            const currentDetails = Array.isArray(this.complaint.complaintOrderComplaintDetails)
                ? this.complaint.complaintOrderComplaintDetails
                : []
            const currentProductTypeByOrderNumber = currentDetails.reduce((accumulator, item) => {
                if (item?.orderNumber) {
                    accumulator[item.orderNumber] = item.productTypeName || null
                }

                return accumulator
            }, {})

            this.complaint.complaintOrderComplaintDetails = selectedOrderNumbers.map(orderNumber => ({
                orderNumber,
                productTypeName: this.orderNumberToProductTypeMap[orderNumber] || currentProductTypeByOrderNumber[orderNumber] || null,
            }))
        },
    },
}
</script>
